// 外包 IP 工作台详情弹框主壳（Offer 谈判详情）。
// 对齐接口 outsourceV2IpCooperationNegotiationDetail（入参 l2_id + round）：打开时加载谈判详情，
// 展示 Header + 锚点区块（产品信息/出价方案）。
// editable=true（当前轮次 + 版权方待确认）时支持逐条处理 + 批量编辑 + 暂存/提交。
// 历史轮次下拉：前端按 current_round 构造占位（后端轮次列表接口就绪后替换），选历史轮次 re-拉详情只读展示。

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Message, Space, Spin } from '@bedrock/components';
import { useSForm } from '@mpc/common/src/components/SForm';
import ContainerDrawer from '@mpc/common/src/components/ContainerDrawer';
import TabsAnchor from '@mpc/common/src/components/TabsAnchor';
import {
  outsourceV2IpCooperationNegotiationDraft,
  outsourceV2IpCooperationNegotiationDetail,
  outsourceV2IpCooperationNegotiationSubmit,
} from 'services/api';
import type {
  IpCooperationCopyrightOwnerDetailVo,
  IpCooperationCopyrightOwnerSubmitForm,
  ItemResult,
} from 'services/api';
import Header from './Header';
import DetailContent, { NegotiationEditState } from './DetailContent';
import StatusProgressDrawer from './StatusProgressDrawer';
import { useDetailDrawerLayout } from './useDetailDrawerLayout';

/** 锚点 tab 标题（与 DetailContent 中 data-is-anchor 区块顺序一致） */
const DETAIL_SECTION_TITLES = ['产品信息', '出价方案'];

interface DetailDrawerProps {
  visible?: boolean;
  /** 二级单 ID（打开详情用） */
  l2Id?: number;
  /** Mock 详情数据（无真实接口数据时用于调试预览；传入后优先于接口加载） */
  mockDetail?: IpCooperationCopyrightOwnerDetailVo | null;
  /** 关闭抽屉 */
  onClose?: () => void;
}

const DetailDrawer: React.FC<DetailDrawerProps> = ({ visible, l2Id, mockDetail, onClose }) => {
  const handleClose = () => onClose?.();
  const handleOpenStatusProgress = () => {
    if (l2Id != null) {
      StatusProgressDrawer.show({ detailId: l2Id });
    }
  };
  // 抽屉 header 实测高度 → --ip-header-h，驱动锚点 tabs sticky 吸附（对齐内包 useHostDrawerLayout）
  const { drawerCssVars } = useDetailDrawerLayout({ visible });
  const [detail, setDetail] = useState<IpCooperationCopyrightOwnerDetailVo | null>(null);
  const [loading, setLoading] = useState(false);
  // 逐条条款编辑状态（key=item_key）
  const [editState, setEditState] = useState<Record<string, NegotiationEditState>>({});
  // 当前选中的谈判轮次（undefined=后端默认当前轮次）
  const [round, setRound] = useState<number | undefined>(undefined);
  // 详情刷新计数（提交/暂存成功后自增触发重新拉取：保持弹框打开展示新状态，而非关闭）
  const [refreshKey, setRefreshKey] = useState(0);
  // 表单实例（FormModel / Form.Item 只读渲染依赖；detail 回填走 setFieldsValue）
  const { form } = useSForm<IpCooperationCopyrightOwnerDetailVo>();

  // 历史轮次列表（前端按 current_round 构造占位；后端轮次接口就绪后改为接口返回）
  const rounds = useMemo(() => {
    const current = detail?.l2_info?.current_round ?? 0;
    if (current <= 0) return [];
    return Array.from({ length: current }, (_, i) => i + 1);
  }, [detail?.l2_info?.current_round]);

  // 记录上次加载键（l2Id/round）：切换轮次（同单 round 变化）保留编辑数据，新开/换单/重开才清空 editState
  const prevLoadKeyRef = useRef<string>('');

  // 打开时加载谈判详情（l2Id/round 变化重新拉取；mockDetail 优先于接口，用于无真实数据时预览）
  useEffect(() => {
    if (!visible) {
      // 关闭抽屉：清空详情/编辑态/轮次数据 + 重置加载键（销毁弹框实例语义，重开时无旧数据残留）
      setDetail(null);
      setEditState({});
      setRound(undefined);
      prevLoadKeyRef.current = '';
      return;
    }
    let cancelled = false;
    const loadKey = `${l2Id ?? 'mock'}:${round ?? ''}`;
    // 同一 l2Id（或 mock）下 round 变化 = 切换历史轮次 → 临时替换详情且保留编辑数据；
    // 否则（新开/换单）清空 editState
    const isRoundSwitch =
      prevLoadKeyRef.current !== '' &&
      prevLoadKeyRef.current !== loadKey &&
      prevLoadKeyRef.current.split(':')[0] === (l2Id != null ? String(l2Id) : 'mock');
    prevLoadKeyRef.current = loadKey;
    const applyDetail = (data: IpCooperationCopyrightOwnerDetailVo | null | undefined) => {
      if (cancelled) return;
      setDetail(data ?? null);
      // 切换轮次临时替换详情时保留 editState（切回当前轮继续回显编辑数据），新开/换单才清空
      if (!isRoundSwitch) setEditState({});
      // 保险/附件回显由 DetailContent formData（need_insurance/negotiation_attachments）经 setFieldsValue 统一回填
      setLoading(false);
    };
    if (mockDetail) {
      applyDetail(mockDetail);
      return () => {
        cancelled = true;
      };
    }
    if (l2Id == null) return;
    setLoading(true);
    outsourceV2IpCooperationNegotiationDetail({ l2_id: l2Id, round })
      .then((res: any) => applyDetail(res?.data))
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, l2Id, mockDetail, round, refreshKey]);

  // 当前轮次可编辑（后端返回 editable）；历史轮次（round 已选且非当前轮）整个页面表单只读，
  // 页面不可编辑（谈判回复/意见/保险/附件/权益均禁用）
  const editable = detail?.editable === true && (round == null || round === (detail?.l2_info?.current_round ?? 0));

  // 单条编辑变更（useCallback 稳定引用：避免 editState 变化时下游表格/FormModel 因回调引用变化阻断 memo）
  const handleEditChange = useCallback((itemKey: string, patch: Partial<NegotiationEditState>) => {
    setEditState((prev) => ({ ...prev, [itemKey]: { ...(prev[itemKey] || {}), ...patch } }));
  }, []);

  // 批量操作：仅针对当前表格实例的 item_keys 标记同一 action（保留已填意见）
  const handleBatchAction = useCallback(
    (action: string, itemKeys?: string[]) => {
      const keys =
        itemKeys && itemKeys.length
          ? itemKeys
          : ((detail?.l2_info?.negotiation_items || []).map((i) => i.item_key).filter(Boolean) as string[]);
      setEditState((prev) => {
        const next = { ...prev };
        keys.forEach((key) => {
          if (!key) return;
          next[key] = { ...(next[key] || {}), action };
        });
        return next;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [detail?.l2_info?.negotiation_items],
  );

  // 底部统计：所有已编辑条款（editState 有 action，含核心商务/游戏方/权益）+ negotiation_items 详情回显（未编辑）
  const stats = useMemo(() => {
    const acc = { ACCEPT: 0, MODIFY: 0, REJECT: 0 };
    const countAction = (action?: string) => {
      if (action && Object.prototype.hasOwnProperty.call(acc, action)) {
        acc[action as keyof typeof acc] += 1;
      }
    };
    // 已编辑条款（editState 覆盖所有表格实例，含游戏方 solution_detail 等固定渲染条款）
    Object.values(editState).forEach((st) => countAction(st?.action));
    // negotiation_items 详情回显（未编辑条款，编辑态已统计则跳过避免重复）
    (detail?.l2_info?.negotiation_items || []).forEach((item) => {
      if (editState[item.item_key || '']?.action) return;
      countAction(item.action);
    });
    return acc;
  }, [detail?.l2_info?.negotiation_items, editState]);

  // 收集逐条操作结果并提交/暂存（Mock 模式不发起真实请求）
  const handleSubmit = async (isDraft: boolean) => {
    if (mockDetail) {
      Message.warn('Mock 数据预览，暂存/提交仅对真实接口生效');
      return;
    }
    if (l2Id == null || !detail) return;
    // 合并收集提交条款：以详情 negotiation_items（已回显的既有回复）为基础，叠加 editState（用户本次编辑覆盖）。
    // 与底部 stats 统计口径一致；后端版权方提交要求全量条款（固定条款 + 权益条款全覆盖，
    // 见 IpCooperationL2ServiceImpl#doCopyrightOwnerSubmit），只收 editState 会报「回显有回复但提交无操作」。
    // 权益条款（item_key=benefit_code）首轮不在 negotiation_items 时，须经 editState 编辑（点回复 Tag/填意见）收集。
    const merged = new Map<string, { item_key: string; action?: string; opinion?: string }>();
    (detail?.l2_info?.negotiation_items || []).forEach((item) => {
      if (!item?.item_key) return;
      merged.set(item.item_key, {
        item_key: item.item_key,
        action: item.action,
        opinion: item.opinion,
      });
    });
    Object.entries(editState).forEach(([itemKey, st]) => {
      if (!itemKey) return;
      const prev = merged.get(itemKey);
      merged.set(itemKey, {
        item_key: itemKey,
        // 编辑态优先；未编辑沿用详情（opinion 编辑态为空时保留详情意见）
        action: st.action ?? prev?.action,
        opinion: st.opinion ?? prev?.opinion,
      });
    });
    // 后端 ItemResult 仅定义 action（回复）+ opinion（意见反馈），修改值（modified_values）暂不提交
    const items: ItemResult[] = Array.from(merged.values())
      .filter((v): v is { item_key: string; action: string; opinion?: string } => !!v.action)
      .map((v) => ({ item_key: v.item_key, action: v.action, opinion: v.opinion }));
    if (!items.length) {
      Message.error('请至少选择一条条款的处理操作');
      return;
    }
    const submitForm: IpCooperationCopyrightOwnerSubmitForm = {
      l2_id: l2Id,
      items,
      // 保险/附件改由 FormModel 受控：need_insurance 详情为 0/1 number，提交转 boolean；
      // 附件存 AttachmentInfo[]/AttachmentDots[]，取 blob.id（新上传）或 id（已存在）转 blob_ids
      need_insurance: form.getFieldValue('need_insurance') === 1,
      blob_ids: (form.getFieldValue('negotiation_attachments') || []).map((a: any) => a?.blob?.id ?? a?.id),
    };
    try {
      if (isDraft) {
        await outsourceV2IpCooperationNegotiationDraft(submitForm);
      } else {
        await outsourceV2IpCooperationNegotiationSubmit(submitForm);
      }
      Message.success(isDraft ? '暂存成功' : '提交成功');
      // 提交/暂存成功后刷新详情（重新拉接口展示新状态：提交后 OFFER_NEGOTIATION_REVIEW 只读），保持弹框打开而非关闭
      setEditState({});
      setRefreshKey((k) => k + 1);
    } catch {
      // 接口错误已统一提示
    }
  };

  return (
    <ContainerDrawer
      className="ip-out-detail-drawer"
      style={drawerCssVars}
      visible={visible}
      header={
        <Header
          detail={detail}
          editable={editable}
          onClose={handleClose}
          onOpenStatusProgress={handleOpenStatusProgress}
        />
      }
      headerStyle={{ padding: '16px 16px 0' }}
      bodyStyle={{ padding: 0, background: '#F0F1F2' }}
      contentWrapperStyle={{ borderLeft: '1px solid #E5E5E5' }}
      bodyScrolled={false}
      onClose={handleClose}
      destroyOnClose
      closable={false}
      footer={
        editable ? (
          // 走查：回复状态汇总信息放「暂存/提交」按钮上方（信息→操作层级），不再与按钮同行
          <div className="flex flex-col items-center gap-2">
            {/* recjcl7M：对齐 figma「接受 3 条，修改 2 条，拒绝 1 条」——逗号分隔、14px、#68768F */}
            <span className="text-[14px] text-[#68768F]">
              接受 <span className={'text-[#21A364]'}>{stats.ACCEPT}</span> 条，修改{' '}
              <span className={'text-[#E08216]'}>{stats.MODIFY}</span> 条，拒绝{' '}
              <span className={'text-[#E55140]'}>{stats.REJECT}</span> 条
            </span>
            <Space size={12}>
              <Button className={'w-[120px]'} onClick={() => handleSubmit(true)}>
                暂存
              </Button>
              <Button className={'w-[120px]'} type="primary" onClick={() => handleSubmit(false)}>
                提交
              </Button>
            </Space>
          </div>
        ) : undefined
      }>
      <TabsAnchor
        className="!h-full"
        // 外包真正的滚动容器是 .rock-drawer-content（Drawer content 区，header+body 同在其中滚动），
        // 非 body/独立内容区——tab 与 header 均相对 content 吸顶
        getAnchorScrollContainer={() =>
          document.querySelector('.ip-out-detail-drawer .rock-drawer-content') as HTMLElement
        }
        // 锚点偏移交给 TabsAnchor 内部 stickyTabsOffset（量吸顶 tabs 高度，px），不再用 header 高度
        tabsProps={{
          size: 'small',
          // sticky 吸顶固定：top 用 header 实测高度（--ip-header-h），z-10 保证不被滚动内容遮挡；
          // [&_.rock-tabs-nav]:mb-0 覆盖 bedrock Tabs nav 默认 margin-bottom 20px，消除 tab 到内容区多余间距
          className:
            'bg-white px-4 ip-out-detail-drawer-header-tab sticky z-10 h-fit flex-none top-[var(--ip-header-h)] [&_.rock-tabs-nav]:mb-0',
          tabListBorder: false,
          // 外包走查：头部 header（Tab 栏）底部需增加分割线，与下方内容卡片形成视觉分组
          tabBarStyle: { borderBottom: '1px solid #E5E5E5', marginBottom: 0 },
        }}
        renderHeaderTab={(item, index) => <span>{DETAIL_SECTION_TITLES[index] ?? item.textContent}</span>}>
        <Spin.Global position="relative" spinning={loading} delay={150}>
          {detail == null ? (
            // 走查：回复列宽度不稳定（打开窄/加载完变宽）——详情未就绪时表格以 editable=false（列宽100）先行渲染，
            // 加载完成 editable=true（列宽180）导致跳变。此处详情就绪前不渲染 DetailContent，仅 loading 占位。
            <div className="flex min-h-[50vh] items-center justify-center">
              <Spin />
            </div>
          ) : (
            <Form form={form} className="h-full" initialValues={{ depth: 1 }}>
              <DetailContent
                detail={detail}
                editable={editable}
                editState={editState}
                onEditChange={handleEditChange}
                round={round}
                rounds={rounds}
                onRoundChange={setRound}
                onBatchAction={handleBatchAction}
                form={form}
              />
            </Form>
          )}
        </Spin.Global>
      </TabsAnchor>
    </ContainerDrawer>
  );
};

export default DetailDrawer;
